
import { supabase } from '@/integrations/supabase/client';
import { OrchestratorAgentResponse, OrchestratorResponseMetadata } from '@/components/calendar/types/orchestration';

interface SupabaseOrchestratorRequest {
  targetAgent: string;
  subIntent: string;
  eventData?: any;
  contextData?: any;
  priority?: 'high' | 'normal' | 'low';
}

export async function invokeSupabaseOrchestrator(
  request: SupabaseOrchestratorRequest
): Promise<OrchestratorAgentResponse> {
  const { targetAgent, subIntent, eventData, contextData, priority = 'normal' } = request;
  
  try {
    console.log(`🚀 Enhanced Supabase Orchestrator: Invoking ${targetAgent}`);
    console.log(`📝 Intent: "${subIntent}"`);
    console.log(`🎯 Priority: ${priority}`);
    
    // Enhanced metadata for intelligent orchestration
    const enhancedMetadata = {
      targetAgent,
      subIntent,
      eventData: {
        ...eventData,
        enhanced_orchestration: true,
        intelligent_analysis_enabled: true,
        a2a_protocol_version: '2.0',
        timestamp: new Date().toISOString()
      },
      contextData,
      priority,
      orchestration_version: '2.0'
    };

    const { data, error } = await supabase.functions.invoke('orchestrator-agent', {
      body: {
        metadata: enhancedMetadata
      }
    });

    if (error) {
      console.error('❌ Enhanced Supabase orchestrator error:', error);
      throw new Error(error.message || 'Failed to communicate with enhanced orchestrator agent');
    }

    if (!data || !data.content) {
      console.error('❌ Invalid response from enhanced orchestrator agent:', data);
      throw new Error('Invalid response from enhanced orchestrator agent');
    }

    console.log('✅ Enhanced Supabase orchestrator response received');
    
    // Log orchestration insights if available
    if (data.metadata?.orchestrated) {
      console.log('🎼 Orchestration insights:', {
        strategy: data.metadata.analysis?.orchestrationStrategy,
        complexity: data.metadata.analysis?.complexity,
        agentsUsed: data.metadata.agents_involved?.length,
        executionTime: data.metadata.performance?.totalExecutionTime,
        quality: data.metadata.performance?.overallQuality
      });
    }

    const responseMetadata: OrchestratorResponseMetadata = {
      status: data.metadata?.status || 'success',
      targetAgent,
      enhanced_orchestration: true,
      intelligent_analysis: data.metadata?.analysis || undefined,
      performance_metrics: data.metadata?.performance || undefined,
      agents_involved: data.metadata?.agents_involved || [],
      orchestration_id: data.metadata?.orchestration_id || undefined,
      a2a_protocol_version: data.metadata?.a2a_protocol_version || '2.0'
    };

    return {
      id: data.id || `enhanced-${Date.now()}`,
      content: data.content,
      metadata: responseMetadata,
    };

  } catch (error) {
    console.error('❌ Error in enhanced Supabase orchestrator:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    const fallbackMetadata: OrchestratorResponseMetadata = {
      status: 'error',
      error: errorMessage,
      targetAgent,
      enhanced_orchestration: true,
      fallback_used: true
    };
    
    return {
      id: `enhanced-fallback-${Date.now()}`,
      content: `I encountered an issue while processing your request with the enhanced orchestration system. Please try again or check your configuration. Error: ${errorMessage}`,
      metadata: fallbackMetadata,
    };
  }
}
